import './style.scss';

import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';

import { deleteById, getAllForAdministration, restore } from 'apis/researchFields.api';
import { Can } from 'components/Can';
import { mapError } from 'utils/func';
import Confirm from './components/Confirm';
import Form from './components/Form';

export default function ResearchFieldViewList() {
  // #region Data
  const { t } = useTranslation();
  const dialogFormRef = useRef(null);
  const dialogConfirmationRef = useRef(null);
  const { mutate: mutateDelete } = useMutation(deleteById);
  const { mutate: mutateRestore } = useMutation(restore);

  const {
    data: _data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(['researchFields-for-administration'], getAllForAdministration);

  const data = useMemo(() => _data?.data ?? [], [_data]);
  // #endregion

  // #region Event
  // #endregion

  // #region Render
  const formatIndex = (_, { rowIndex }) => rowIndex + 1;

  const formatActions = (value) => (
    <div className="flex justify-around gap-1">
      {value?.deletedAt === null ? (
        <>
          <Can do="update" on="research_field">
            <Button
              severity="warning"
              tooltip={t('researchField.update.title')}
              tooltipOptions={{ position: 'left' }}
              icon="pi pi-pencil"
              onClick={() => {
                dialogFormRef.current.open({
                  mode: 'UPDATE',
                  value,
                });
              }}
            />
          </Can>
          <Can do="delete" on="research_field">
            <Button
              severity="danger"
              tooltip={t('researchField.delete.title')}
              tooltipOptions={{ position: 'left' }}
              icon="pi pi-trash"
              onClick={() => {
                dialogConfirmationRef.current.open({
                  mode: 'delete',
                  mutate: mutateDelete,
                  value,
                });
              }}
            />
          </Can>
        </>
      ) : (
        <Can do="restore" on="research_field">
          <Button
            icon="pi pi-undo"
            severity="danger"
            tooltip={t('researchField.restore.title')}
            tooltipOptions={{ position: 'left' }}
            onClick={() =>
              dialogConfirmationRef.current.open({
                mode: 'restore',
                mutate: mutateRestore,
                value,
              })
            }
          />
        </Can>
      )}
    </div>
  );
  // #endregion Render

  return (
    <div className="card relative">
      {isError && <h3 className="text-red-600">{mapError(t, error)}</h3>}
      <div className="flex justify-content-between align-items-center pb-4">
        <h4 className="m-0 hidden sm:block text-xl sm:text-2xl">
          {t('researchField.viewList.title')}
        </h4>
        <Can do="create" on="research_field">
          <Button
            className="bg-cyan-600 border-cyan-600"
            label={t('researchField.viewList.add')}
            icon="pi pi-plus"
            onClick={() => {
              dialogFormRef.current.open({
                mode: 'CREATE',
              });
            }}
          />
        </Can>
      </div>

      <DataTable
        value={data}
        className="p-datatable-gridlines header-table"
        showGridlines
        dataKey="id"
        loading={isLoading}
        responsiveLayout="scroll"
        emptyMessage={t('researchField.viewList.emptyMessage')}
      >
        <Column
          header={t('researchField.viewList.no')}
          body={formatIndex}
          className="text-center w-5rem"
          frozen
        />
        <Column
          header={t('researchField.name')}
          field="name"
          className="w-30rem"
          style={{ minWidth: '14rem' }}
          frozen
        />
        <Column
          header={t('researchField.englishName')}
          field="englishName"
          className="w-30rem"
          style={{ minWidth: '14rem' }}
          frozen
        />
        <Column
          header={t('projectProposal.action')}
          body={formatActions}
          className="w-8rem text-center"
          frozen
          alignFrozen="right"
        />
      </DataTable>

      <Form ref={dialogFormRef} refetch={refetch} />

      <Can do="delete" on="research_field">
        <Confirm ref={dialogConfirmationRef} refetch={refetch} />
      </Can>
    </div>
  );
}
